import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  MenuItem,
  Button,
  styled
} from '@mui/material';
import { Refresh as RefreshIcon, Search as SearchIcon, Download as DownloadIcon } from '@mui/icons-material';
import InputAdornment from '@mui/material/InputAdornment';
import { format } from 'date-fns';
import { Navigate } from 'react-router-dom';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonatePlainIconButtonSx,
  harmonateProceedButtonSx,
  harmonateToolbarButtonSx,
  harmonateToolbarDateFieldSx,
  harmonateToolbarFieldSx,
  harmonateToolbarSx,
  harmonatePageSearchFieldSx,
  harmonatePageSearchIconSx,
  harmonateSelectMenuProps,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';
import { logAudit } from '../../../utils/auditLogger';
import { isHarmonateUser as isServerHarmonateUser } from '../../../auth/serverIdentity';

/**
 * Audit log writers send UTC via toISOString(). API often returns that wall-clock
 * without a Z/offset (LocalDateTime). Treat naive ISO as UTC, then show browser local
 * time — same approach as Document Details audit logs (toLocaleString).
 */
const formatAuditLogDateTime = (value) => {
  if (!value) return 'N/A';
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? 'N/A' : value.toLocaleString();
  }
  const raw = String(value).trim();
  if (!raw) return 'N/A';
  const hasTimezone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(raw);
  const normalized =
    !hasTimezone && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw)
      ? `${raw.replace(/\.\d+$/, '')}Z`
      : raw;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? 'N/A' : date.toLocaleString();
};

const ResizableTableCell = styled(TableCell)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(1, 2),
  '&:last-child': {
    borderRight: 'none',
  },
}));

const Resizer = styled('div')({
  position: 'absolute',
  right: 0,
  top: 0,
  height: '100%',
  width: '10px',
  cursor: 'col-resize',
  zIndex: 1,
  '&:hover': {
    backgroundColor: ht.secondaryTab,
  },
  '&.active': {
    backgroundColor: ht.blue,
  },
});

/**
 * @param {'tenant' | 'application'} mode
 * - tenant: Configurations view (current tenant; Harmonate actors hidden for normal users)
 * - application: Harmonate menu (all app logs; tenant + user filters)
 */
const UserLogs = ({ mode = 'tenant' }) => {
  const isApplicationMode = mode === 'application';
  const isHarmonateUser = isServerHarmonateUser();
  const sessionTenantId = localStorage.getItem('tenant_id');
  const { canExport } = useFunctionPermissions(FC.AUDIT_LOGS);

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [filters, setFilters] = useState({
    search: '',
    user: '',
    tenantId: '',
  });
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportNotice, setExportNotice] = useState(null);

  const [columnWidths, setColumnWidths] = useState({
    tenant: 160,
    user: 120,
    date: 200,
    action: 100,
    function: 180,
    file: 200,
    message: 400
  });

  const [isResizing, setIsResizing] = useState(false);
  const [currentCol, setCurrentCol] = useState(null);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);

  const tenantNameById = useMemo(() => {
    const map = {};
    tenants.forEach((t) => {
      if (t?.id != null) {
        map[String(t.id)] = t.name || String(t.id);
      }
    });
    return map;
  }, [tenants]);

  const resolveTenantLabel = useCallback((tenantId) => {
    if (!tenantId) return 'N/A';
    return tenantNameById[String(tenantId)] || tenantId;
  }, [tenantNameById]);

  const authHeaders = () => {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
  };

  const fetchTenants = useCallback(async () => {
    if (!isApplicationMode) return;
    try {
      setTenantsLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/tenants`, {
        headers: authHeaders(),
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch tenants: ${response.status}`);
      }
      const data = await response.json();
      setTenants(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching tenants:', err);
      setTenants([]);
    } finally {
      setTenantsLoading(false);
    }
  }, [isApplicationMode]);

  const fetchUsers = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        scope: isApplicationMode ? 'application' : 'tenant',
      });
      if (isApplicationMode) {
        if (filters.tenantId) {
          params.append('tenantId', filters.tenantId);
        }
      } else if (sessionTenantId) {
        params.append('tenantId', sessionTenantId);
      } else {
        setUsers([]);
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/logs/usernames?${params.toString()}`,
        { headers: authHeaders() }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching users:', err);
      setUsers([]);
    }
  }, [isApplicationMode, filters.tenantId, sessionTenantId]);

  const buildLogParams = useCallback((includePaging) => {
    const params = new URLSearchParams({
      scope: isApplicationMode ? 'application' : 'tenant',
    });

    if (includePaging) {
      params.set('page', String(page));
      params.set('size', '1000');
    }

    if (isApplicationMode) {
      if (filters.tenantId) {
        params.append('tenantId', filters.tenantId);
      }
    } else if (sessionTenantId) {
      params.append('tenantId', sessionTenantId);
    }

    if (filters.user) {
      params.append('username', filters.user);
    }

    if (dateRange.start) {
      const startDate = new Date(dateRange.start);
      startDate.setHours(0, 0, 0, 0);
      params.append('startDate', startDate.toISOString().split('.')[0]);
    }

    if (dateRange.end) {
      const endDate = new Date(dateRange.end);
      endDate.setHours(23, 59, 59, 999);
      params.append('endDate', endDate.toISOString().split('.')[0]);
    }

    return params;
  }, [
    isApplicationMode,
    page,
    filters.tenantId,
    filters.user,
    dateRange.start,
    dateRange.end,
    sessionTenantId,
  ]);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!isApplicationMode && !sessionTenantId) {
        setError('No tenant ID found');
        setLogs([]);
        setLoading(false);
        return;
      }

      const params = buildLogParams(true);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/logs?${params.toString()}`,
        {
          method: 'GET',
          headers: authHeaders(),
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response received:', text);
        throw new Error('Received non-JSON response from server');
      }

      const data = await response.json();
      const logsArray = Array.isArray(data.content)
        ? data.content
        : (Array.isArray(data) ? data : null);

      if (!logsArray) {
        throw new Error('Invalid data format: could not find logs array');
      }

      const formattedLogs = logsArray.map((log) => ({
        ...log,
        id: log.id ? log.id.toString() : Math.random().toString(36).substr(2, 9),
        displayDate: formatAuditLogDateTime(log.dateTime),
        displayFileName: log.fileName || 'N/A',
      }));

      if (data.totalElements !== undefined) {
        setTotalItems(data.totalElements);
      }
      if (data.totalPages !== undefined) {
        setTotalPages(data.totalPages);
      }

      setLogs(formattedLogs);
    } catch (err) {
      console.error('Error fetching logs:', err);
      setError(err.message || 'Failed to fetch logs');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [isApplicationMode, sessionTenantId, buildLogParams]);

  useEffect(() => {
    if (isApplicationMode) {
      fetchTenants();
    }
  }, [isApplicationMode, fetchTenants]);

  useEffect(() => {
    if (isApplicationMode || sessionTenantId) {
      fetchUsers();
    }
  }, [isApplicationMode, sessionTenantId, filters.tenantId, fetchUsers]);

  useEffect(() => {
    if (isApplicationMode || sessionTenantId) {
      fetchLogs();
    } else {
      setError('No tenant ID found');
      setLoading(false);
    }
  }, [isApplicationMode, sessionTenantId, fetchLogs]);

  const handleLoadData = () => {
    setPage(0);
    fetchLogs();
    fetchUsers();
  };

  const handleExport = async () => {
    if (!canExport || exporting) return;
    if (!isApplicationMode && !sessionTenantId) {
      setExportNotice({ severity: 'error', message: 'No tenant ID found' });
      return;
    }

    setExporting(true);
    setExportNotice(null);
    try {
      const params = buildLogParams(false);
      const search = filters.search.trim();
      if (search) {
        params.append('search', search);
      }
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (timeZone) {
        params.append('timeZone', timeZone);
      }
      if (navigator.language) {
        params.append('locale', navigator.language);
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/logs/export?${params.toString()}`,
        {
          method: 'GET',
          headers: authHeaders(),
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const text = await response.text();
        let message = `Export failed (${response.status})`;
        try {
          const body = JSON.parse(text);
          message = body.message || body.error || message;
        } catch {
          if (text) message = text.slice(0, 300);
        }
        throw new Error(message);
      }

      const blob = await response.blob();
      const tenantPart = isApplicationMode
        ? (filters.tenantId ? resolveTenantLabel(filters.tenantId) : 'all-tenants')
        : 'tenant';
      const safeTenant = tenantPart.replace(/[^\w.-]+/g, '_');
      let rangePart = 'all';
      if (dateRange.start && dateRange.end) {
        rangePart = `${format(dateRange.start, 'yyyy-MM-dd')}_to_${format(dateRange.end, 'yyyy-MM-dd')}`;
      } else if (dateRange.start) {
        rangePart = `from_${format(dateRange.start, 'yyyy-MM-dd')}`;
      } else if (dateRange.end) {
        rangePart = `until_${format(dateRange.end, 'yyyy-MM-dd')}`;
      }
      const filename = `audit-logs_${safeTenant}_${rangePart}.csv`;

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      const userPart = filters.user ? ` for user ${filters.user}` : '';
      const searchPart = search ? ` matching "${search}"` : '';
      const rangeText = rangePart === 'all' ? 'all dates' : rangePart.replace(/_/g, ' ');
      await logAudit({
        message: `Exported audit logs (${rangeText})${userPart}${searchPart}`,
        action: 'export',
        functionName: 'Audit Logs',
        fileName: filename,
        details: filters.tenantId ? { tenantId: filters.tenantId } : {},
      });

      setExportNotice({ severity: 'success', message: `Downloaded ${filename}` });
    } catch (err) {
      console.error('Error exporting logs:', err);
      setExportNotice({
        severity: 'error',
        message: err.message || 'Failed to export audit logs',
      });
    } finally {
      setExporting(false);
    }
  };

  const handleNextPage = () => {
    setPage((prevPage) => prevPage + 1);
  };

  const handlePrevPage = () => {
    setPage((prevPage) => Math.max(0, prevPage - 1));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    setPage(0);
  };

  function handleRefresh() {
    fetchLogs();
  }

  const handleUserChange = (event) => {
    setFilters((prev) => ({ ...prev, user: event.target.value }));
    setPage(0);
  };

  const handleTenantChange = (event) => {
    setFilters((prev) => ({
      ...prev,
      tenantId: event.target.value,
      user: '',
    }));
    setPage(0);
  };

  const handleDateChange = (type) => (event) => {
    const date = event.target.value ? new Date(event.target.value) : null;
    setDateRange((prev) => ({
      ...prev,
      [type]: date,
    }));
    setPage(0);
  };

  const startResize = (e, colName) => {
    setIsResizing(true);
    setCurrentCol(colName);
    setStartX(e.pageX);
    setStartWidth(columnWidths[colName]);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const onResize = (e) => {
    if (!isResizing) return;
    const newWidth = startWidth + (e.pageX - startX);
    setColumnWidths((prev) => ({
      ...prev,
      [currentCol]: Math.max(50, newWidth),
    }));
  };

  const stopResize = () => {
    setIsResizing(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  };

  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', onResize);
      window.addEventListener('mouseup', stopResize);
      return () => {
        window.removeEventListener('mousemove', onResize);
        window.removeEventListener('mouseup', stopResize);
      };
    }
  }, [isResizing, startX, startWidth, currentCol]);

  const filteredLogs = logs.filter((log) => {
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const tenantLabel = resolveTenantLabel(log.tenantId).toLowerCase();
      return (
        (log.userName?.toLowerCase() || '').includes(searchLower) ||
        (log.action?.toLowerCase() || '').includes(searchLower) ||
        (log.message?.toLowerCase() || '').includes(searchLower) ||
        (log.functionName?.toLowerCase() || '').includes(searchLower) ||
        tenantLabel.includes(searchLower) ||
        (String(log.tenantId || '').toLowerCase()).includes(searchLower)
      );
    }
    return true;
  });

  // API already returns the current page; search filter is client-side on that page
  const displayLogs = filteredLogs;
  const colSpan = isApplicationMode ? 7 : 6;

  const exportFilterBits = [];
  if (dateRange.start && dateRange.end) {
    exportFilterBits.push(`from ${format(dateRange.start, 'MM/dd/yyyy')} to ${format(dateRange.end, 'MM/dd/yyyy')}`);
  } else if (dateRange.start) {
    exportFilterBits.push(`from ${format(dateRange.start, 'MM/dd/yyyy')}`);
  } else if (dateRange.end) {
    exportFilterBits.push(`until ${format(dateRange.end, 'MM/dd/yyyy')}`);
  }
  if (isApplicationMode && filters.tenantId) {
    exportFilterBits.push(`for ${resolveTenantLabel(filters.tenantId)}`);
  }
  if (filters.user) {
    exportFilterBits.push(`for user ${filters.user}`);
  }
  if (filters.search.trim()) {
    exportFilterBits.push(`matching "${filters.search.trim()}"`);
  }
  const exportTooltip = !canExport
    ? 'No export permission'
    : exportFilterBits.length === 0
      ? 'Downloads every audit log as CSV, not only the rows on this page.'
      : `Downloads every audit log ${exportFilterBits.join(' ')}, not only the rows on this page.`;

  if (isApplicationMode && !isHarmonateUser) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Box sx={{ ...harmonatePageSx, height: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={harmonatePageTitleSx}>
          {isApplicationMode ? 'Audit Logs (Application)' : 'Audit Logs'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Box sx={harmonateToolbarSx}>
            <TextField
              type="date"
              label="Start Date"
              size="small"
              value={dateRange.start ? format(dateRange.start, 'yyyy-MM-dd') : ''}
              onChange={handleDateChange('start')}
              InputLabelProps={{ shrink: true }}
              sx={harmonateToolbarDateFieldSx}
            />
            <TextField
              type="date"
              label="End Date"
              size="small"
              value={dateRange.end ? format(dateRange.end, 'yyyy-MM-dd') : ''}
              onChange={handleDateChange('end')}
              InputLabelProps={{ shrink: true }}
              sx={harmonateToolbarDateFieldSx}
            />
            {isApplicationMode && (
              <TextField
                select
                label="Tenant"
                size="small"
                value={filters.tenantId}
                onChange={handleTenantChange}
                sx={{ minWidth: 180, ...harmonateToolbarFieldSx }}
                disabled={loading || tenantsLoading}
                SelectProps={{ MenuProps: harmonateSelectMenuProps }}
              >
                <MenuItem value="">
                  <em>All Tenants</em>
                </MenuItem>
                {tenants.map((tenant) => (
                  <MenuItem key={tenant.id} value={tenant.id}>
                    {tenant.name || tenant.id}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              select
              label="User"
              size="small"
              value={filters.user}
              onChange={handleUserChange}
              sx={{ minWidth: 150, ...harmonateToolbarFieldSx }}
              disabled={loading}
              SelectProps={{ MenuProps: harmonateSelectMenuProps }}
            >
              <MenuItem value="">
                <em>All Users</em>
              </MenuItem>
              {users.map((user) => (
                <MenuItem key={user} value={user}>
                  {user}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              variant="outlined"
              placeholder="Search logs..."
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              size="small"
              disabled={loading}
              sx={harmonatePageSearchFieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={harmonatePageSearchIconSx} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={handleLoadData}
              disabled={loading}
              startIcon={<RefreshIcon />}
              sx={harmonateProceedButtonSx}
            >
              Load Data
            </Button>
            <Tooltip title={exportTooltip}>
              <span>
                <Button
                  variant="outlined"
                  onClick={handleExport}
                  disabled={loading || exporting || !canExport}
                  startIcon={exporting ? <CircularProgress size={16} sx={{ color: ht.text }} /> : <DownloadIcon />}
                  sx={harmonateToolbarButtonSx}
                >
                  {exporting ? 'Exporting' : 'Export'}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Refresh">
              <span>
                <IconButton onClick={handleRefresh} disabled={loading} sx={harmonatePlainIconButtonSx}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="outlined"
              onClick={handlePrevPage}
              disabled={page === 0 || loading}
              sx={harmonateToolbarButtonSx}
            >
              Previous
            </Button>
            <Button
              variant="outlined"
              onClick={handleNextPage}
              disabled={loading || (totalPages > 0 && page >= totalPages - 1)}
              sx={harmonateToolbarButtonSx}
            >
              Next
            </Button>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', color: ht.white }}>
              Page {page + 1}{totalPages > 0 ? ` of ${totalPages}` : ''}
              {totalItems > 0 ? ` (${totalItems})` : ''}
            </Typography>
          </Box>
        </Box>
      </Box>

      {exportNotice && (
        <Alert
          severity={exportNotice.severity}
          onClose={() => setExportNotice(null)}
          sx={{ mb: 2 }}
        >
          {exportNotice.message}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          ...harmonateCardSx,
          flex: 1,
          minHeight: 0,
          width: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={5}>
            <CircularProgress sx={{ color: ht.text }} />
          </Box>
        ) : error ? (
          <Box p={3}>
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </Box>
        ) : (
          <TableContainer sx={{ height: 'calc(100vh - 240px)', bgcolor: ht.white }}>
            <Table
              stickyHeader
              size="small"
              sx={{
                '& .MuiTableHead-root .MuiTableCell-root': {
                  bgcolor: ht.white,
                  color: ht.text,
                  fontWeight: 700,
                  fontSize: '0.625rem',
                  borderBottom: `1px solid ${ht.line}`,
                },
                '& .MuiTableBody-root .MuiTableRow-root:hover': {
                  bgcolor: ht.secondaryTab,
                },
                '& .MuiTableBody-root .MuiTableCell-root': {
                  fontSize: '0.75rem',
                  color: ht.text,
                  borderBottom: `1px solid ${ht.line}`,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  {isApplicationMode && (
                    <ResizableTableCell style={{ width: columnWidths.tenant }}>
                      Tenant
                      <Resizer
                        onMouseDown={(e) => startResize(e, 'tenant')}
                        className={isResizing && currentCol === 'tenant' ? 'active' : ''}
                      />
                    </ResizableTableCell>
                  )}
                  <ResizableTableCell style={{ width: columnWidths.user }}>
                    User
                    <Resizer
                      onMouseDown={(e) => startResize(e, 'user')}
                      className={isResizing && currentCol === 'user' ? 'active' : ''}
                    />
                  </ResizableTableCell>
                  <ResizableTableCell style={{ width: columnWidths.date }}>
                    Date & Time
                    <Resizer
                      onMouseDown={(e) => startResize(e, 'date')}
                      className={isResizing && currentCol === 'date' ? 'active' : ''}
                    />
                  </ResizableTableCell>
                  <ResizableTableCell style={{ width: columnWidths.action }}>
                    Action
                    <Resizer
                      onMouseDown={(e) => startResize(e, 'action')}
                      className={isResizing && currentCol === 'action' ? 'active' : ''}
                    />
                  </ResizableTableCell>
                  <ResizableTableCell style={{ width: columnWidths.function }}>
                    Function
                    <Resizer
                      onMouseDown={(e) => startResize(e, 'function')}
                      className={isResizing && currentCol === 'function' ? 'active' : ''}
                    />
                  </ResizableTableCell>
                  <ResizableTableCell style={{ width: columnWidths.file }}>
                    File
                    <Resizer
                      onMouseDown={(e) => startResize(e, 'file')}
                      className={isResizing && currentCol === 'file' ? 'active' : ''}
                    />
                  </ResizableTableCell>
                  <ResizableTableCell style={{ width: columnWidths.message }}>
                    Message
                    <Resizer
                      onMouseDown={(e) => startResize(e, 'message')}
                      className={isResizing && currentCol === 'message' ? 'active' : ''}
                    />
                  </ResizableTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayLogs.length > 0 ? (
                  displayLogs.map((log) => (
                    <TableRow key={log.id} hover>
                      {isApplicationMode && (
                        <ResizableTableCell style={{ width: columnWidths.tenant }}>
                          {resolveTenantLabel(log.tenantId)}
                        </ResizableTableCell>
                      )}
                      <ResizableTableCell style={{ width: columnWidths.user }}>
                        {log.userName || 'N/A'}
                      </ResizableTableCell>
                      <ResizableTableCell style={{ width: columnWidths.date }}>
                        {formatAuditLogDateTime(log.dateTime)}
                      </ResizableTableCell>
                      <ResizableTableCell style={{ width: columnWidths.action }}>
                        <Chip
                          label={log.action}
                          size="small"
                          sx={{
                            height: 24,
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            bgcolor: ht.listBg,
                            color: ht.text,
                            borderRadius: ht.buttonRadius,
                            '& .MuiChip-label': { px: 1.5 },
                          }}
                        />
                      </ResizableTableCell>
                      <ResizableTableCell style={{ width: columnWidths.function }}>
                        {log.functionName || 'N/A'}
                      </ResizableTableCell>
                      <ResizableTableCell style={{ width: columnWidths.file }}>
                        {log.displayFileName}
                      </ResizableTableCell>
                      <ResizableTableCell style={{ width: columnWidths.message }}>
                        <Tooltip title={log.message || ''}>
                          <Box
                            sx={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '700px',
                              width: '100%',
                            }}
                          >
                            {log.message || 'N/A'}
                          </Box>
                        </Tooltip>
                      </ResizableTableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={colSpan} align="center">
                      No logs found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default UserLogs;
